import { motion } from 'framer-motion';

import type { ServiceCategory } from '@/data/services';

interface ServiceIllustrationProps {
  category: ServiceCategory;
  className?: string | undefined;
}

const ease = [0.4, 0, 0.2, 1] as const;

export function ServiceIllustration({
  category,
  className,
}: ServiceIllustrationProps): React.ReactNode {
  if (category === 'baumpflege') {
    return <PflegeIllustration className={className} />;
  }
  if (category === 'baumhausbau') {
    return <HausbauIllustration className={className} />;
  }
  return <FaellungIllustration className={className} />;
}

function PflegeIllustration({ className }: { className?: string | undefined }): React.ReactNode {
  return (
    <svg
      viewBox="0 0 120 120"
      className={className}
      role="img"
      aria-label="Baumpflege-Illustration"
    >
      {/* Trunk */}
      <rect x="55" y="60" width="10" height="50" rx="2" fill="#1c2218" />
      {/* Crown — three overlapping organic shapes for richness */}
      <motion.ellipse
        cx="42"
        cy="50"
        rx="22"
        ry="20"
        fill="#3d4a32"
        animate={{ rx: [22, 23, 22], ry: [20, 21, 20] }}
        transition={{ duration: 6, repeat: Infinity, ease }}
      />
      <motion.ellipse
        cx="78"
        cy="48"
        rx="20"
        ry="22"
        fill="#3d4a32"
        animate={{ rx: [20, 21, 20], ry: [22, 23, 22] }}
        transition={{ duration: 7, repeat: Infinity, ease, delay: 0.3 }}
      />
      <motion.ellipse
        cx="60"
        cy="38"
        rx="22"
        ry="22"
        fill="#7a9b5b"
        animate={{ rx: [22, 23, 22], ry: [22, 23, 22] }}
        transition={{ duration: 5.5, repeat: Infinity, ease, delay: 0.6 }}
      />

      {/* Falling leaves */}
      {[
        { cx: 30, delay: 0, drift: -4 },
        { cx: 92, delay: 1.2, drift: 6 },
        { cx: 55, delay: 2.4, drift: 2 },
      ].map((leaf) => (
        <motion.g
          key={`${String(leaf.cx)}-${String(leaf.delay)}`}
          initial={{ y: -30, x: 0, opacity: 0, rotate: 0 }}
          animate={{ y: 60, x: leaf.drift * 4, opacity: [0, 1, 1, 0], rotate: 360 }}
          transition={{ duration: 4, repeat: Infinity, delay: leaf.delay, ease: 'easeIn' }}
        >
          <ellipse cx={leaf.cx} cy="45" rx="3.5" ry="2" fill="#7a9b5b" />
        </motion.g>
      ))}

      {/* Scissors — schnipp schnipp */}
      <motion.g
        animate={{ rotate: [0, -18, 0, 0, 0], x: [0, -2, 0, 0, 0] }}
        transition={{ duration: 3.5, repeat: Infinity, ease, times: [0, 0.15, 0.3, 0.7, 1] }}
        style={{ transformOrigin: '95px 38px' }}
      >
        <circle cx="92" cy="36" r="3" fill="none" stroke="#1c2218" strokeWidth="1.6" />
        <circle cx="98" cy="42" r="3" fill="none" stroke="#1c2218" strokeWidth="1.6" />
        <line x1="92" y1="38" x2="105" y2="50" stroke="#1c2218" strokeWidth="1.6" />
        <line x1="98" y1="44" x2="105" y2="50" stroke="#1c2218" strokeWidth="1.6" />
      </motion.g>
    </svg>
  );
}

function HausbauIllustration({ className }: { className?: string | undefined }): React.ReactNode {
  return (
    <svg
      viewBox="0 0 120 120"
      className={className}
      role="img"
      aria-label="Baumhausbau-Illustration"
    >
      {/* Trunk */}
      <rect x="55" y="35" width="10" height="80" rx="2" fill="#1c2218" />
      {/* Crown above */}
      <ellipse cx="60" cy="25" rx="32" ry="20" fill="#3d4a32" />
      <ellipse cx="60" cy="20" rx="22" ry="14" fill="#7a9b5b" />

      {/* Treehouse — slides in from right then settles */}
      <motion.g
        initial={{ x: 40, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{
          duration: 0.9,
          ease,
          repeat: Infinity,
          repeatType: 'reverse',
          repeatDelay: 2,
        }}
      >
        {/* Platform */}
        <rect x="25" y="68" width="55" height="6" rx="1" fill="#8b6f47" />
        {/* House body */}
        <rect x="32" y="50" width="42" height="20" fill="#8b6f47" />
        {/* Roof */}
        <polygon points="28,52 53,38 78,52" fill="#1c2218" />
        {/* Window */}
        <rect x="44" y="56" width="10" height="9" fill="#eef1de" />
        <line x1="49" y1="56" x2="49" y2="65" stroke="#8b6f47" strokeWidth="1" />
        {/* Ladder rungs */}
        <line x1="38" y1="74" x2="38" y2="100" stroke="#8b6f47" strokeWidth="1.5" />
        <line x1="46" y1="74" x2="46" y2="100" stroke="#8b6f47" strokeWidth="1.5" />
        {[78, 84, 90, 96].map((y) => (
          <line key={y} x1="38" y1={y} x2="46" y2={y} stroke="#8b6f47" strokeWidth="1.2" />
        ))}
      </motion.g>

      {/* Hammer — taps */}
      <motion.g
        animate={{ rotate: [0, -25, 0, -25, 0, 0, 0] }}
        transition={{
          duration: 2.5,
          repeat: Infinity,
          ease,
          times: [0, 0.1, 0.2, 0.3, 0.4, 0.7, 1],
        }}
        style={{ transformOrigin: '90px 56px' }}
      >
        <line x1="90" y1="56" x2="86" y2="48" stroke="#1c2218" strokeWidth="2" />
        <rect x="83" y="44" width="8" height="5" fill="#1c2218" />
      </motion.g>
    </svg>
  );
}

function FaellungIllustration({ className }: { className?: string | undefined }): React.ReactNode {
  return (
    <svg
      viewBox="0 0 120 120"
      className={className}
      role="img"
      aria-label="Baumfällung-Illustration"
    >
      {/* Trunk (standing — saw cuts into it without falling) */}
      <rect x="53" y="30" width="8" height="70" rx="1" fill="#1c2218" />
      {/* Crown */}
      <ellipse cx="57" cy="22" rx="22" ry="18" fill="#3d4a32" />
      <ellipse cx="57" cy="18" rx="14" ry="12" fill="#7a9b5b" />

      {/* Subtle trunk-vibration mark on the saw line */}
      <motion.line
        x1="53"
        y1="64"
        x2="61"
        y2="64"
        stroke="#a08560"
        strokeWidth="1.5"
        animate={{ opacity: [0, 0.8, 0] }}
        transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
      />

      {/* Saw — moves back and forth on the trunk */}
      <motion.g
        animate={{ x: [0, 6, 0, 6, 0, 0] }}
        transition={{
          duration: 1.6,
          repeat: Infinity,
          ease: 'easeInOut',
          times: [0, 0.15, 0.3, 0.45, 0.6, 1],
        }}
      >
        <rect x="32" y="70" width="20" height="3" rx="1" fill="#1c2218" />
        <rect x="22" y="68" width="12" height="7" rx="1" fill="#3d4a32" />
        {/* Teeth */}
        {[34, 38, 42, 46, 50].map((x) => (
          <polygon
            key={x}
            points={`${String(x)},73 ${String(x + 1)},75 ${String(x + 2)},73`}
            fill="#1c2218"
          />
        ))}
      </motion.g>

      {/* Sawdust particles */}
      {[
        { delay: 0.3, x: 38 },
        { delay: 0.9, x: 44 },
        { delay: 1.5, x: 41 },
      ].map((d) => (
        <motion.circle
          key={`${String(d.delay)}-${String(d.x)}`}
          cx={d.x}
          cy="70"
          r="1"
          fill="#a08560"
          initial={{ opacity: 0, y: 0 }}
          animate={{ opacity: [0, 1, 0], y: [0, 8, 14] }}
          transition={{ duration: 1.4, repeat: Infinity, delay: d.delay, ease: 'easeOut' }}
        />
      ))}
    </svg>
  );
}
